import * as React from 'react';
import { Info, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import { cn } from './cn';

type AlertTone = 'info' | 'success' | 'warning' | 'danger';

const TONE: Record<AlertTone, { wrap: string; icon: React.ReactNode }> = {
  info: { wrap: 'border-eds-info/30 bg-eds-info/8 text-eds-fg', icon: <Info className="h-4 w-4 text-eds-info" /> },
  success: { wrap: 'border-eds-success/30 bg-eds-success/8 text-eds-fg', icon: <CheckCircle2 className="h-4 w-4 text-eds-emerald-2" /> },
  warning: { wrap: 'border-eds-warning/40 bg-eds-warning/10 text-eds-fg', icon: <AlertTriangle className="h-4 w-4 text-eds-earth" /> },
  danger: { wrap: 'border-eds-danger/30 bg-eds-danger/8 text-eds-fg', icon: <XCircle className="h-4 w-4 text-eds-danger" /> },
};

export interface AlertProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  tone?: AlertTone;
  title?: React.ReactNode;
  icon?: React.ReactNode;
}

/** EDS inline alert / banner — Design Bible §7.9. Uses role="status" (polite). */
export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ className, tone = 'info', title, icon, children, ...props }, ref) => {
    const t = TONE[tone];
    return (
      <div
        ref={ref}
        role="status"
        className={cn('flex items-start gap-2.5 rounded-eds-md border px-4 py-3 font-eds-body text-sm', t.wrap, className)}
        {...props}
      >
        <span className="mt-0.5 shrink-0" aria-hidden="true">
          {icon ?? t.icon}
        </span>
        <div className="min-w-0">
          {title && <p className="font-semibold text-eds-fg">{title}</p>}
          {children && <div className="text-eds-muted-fg">{children}</div>}
        </div>
      </div>
    );
  },
);
Alert.displayName = 'EdsAlert';

export default Alert;
